import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import heroDashboardDark from "@/assets/hero-dashboard-preview.png";
import heroDashboardLight from "@/assets/hero-dashboard-light.png";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { useHotspots, Hotspot } from "@/hooks/useHotspots";
import { HotspotAdminDialog } from "@/components/HotspotAdminDialog";
import { HotspotEditor } from "@/components/HotspotEditor";

const HotspotPoint = ({ hotspot }: { hotspot: Hotspot }) => {
  const [isHovered, setIsHovered] = useState(false);
  const tooltipBelow = hotspot.tooltip_position === "bottom";
  const tooltipLeft = hotspot.tooltip_position === "left";
  const tooltipRight = hotspot.tooltip_position === "right";

  const getTooltipPosition = () => {
    if (tooltipBelow) return "top-full mt-3";
    if (tooltipLeft) return "right-full mr-3 top-1/2 -translate-y-1/2";
    if (tooltipRight) return "left-full ml-3 top-1/2 -translate-y-1/2";
    return "bottom-full mb-3"; // top (default)
  };

  const getTooltipTransform = () => {
    if (tooltipLeft || tooltipRight) {
      return isHovered 
        ? "opacity-100 translate-x-0" 
        : tooltipLeft 
          ? "opacity-0 translate-x-2 pointer-events-none"
          : "opacity-0 -translate-x-2 pointer-events-none";
    }
    return isHovered 
      ? "opacity-100 translate-y-0" 
      : tooltipBelow 
        ? "opacity-0 -translate-y-2 pointer-events-none"
        : "opacity-0 translate-y-2 pointer-events-none";
  };

  return (
    <div
      className="absolute z-10 group"
      style={{ top: `${hotspot.top_percent}%`, left: `${hotspot.left_percent}%` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Pulsing outer ring */}
      <div className="absolute -inset-2 bg-primary/30 rounded-full animate-ping" />
      
      {/* Inner dot */}
      <div className="relative w-4 h-4 bg-primary rounded-full cursor-pointer border-2 border-white shadow-lg hover:scale-125 transition-transform duration-200" />
      
      {/* Tooltip */}
      <div
        className={`absolute ${tooltipLeft || tooltipRight ? '' : 'left-1/2 -translate-x-1/2'} whitespace-nowrap transition-all duration-200 ${getTooltipPosition()} ${getTooltipTransform()}`}
      >
        {/* Tooltip arrow */}
        {tooltipBelow && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-foreground" />
        )}
        {tooltipLeft && (
          <div className="absolute right-0 translate-x-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-foreground" />
        )}
        {tooltipRight && (
          <div className="absolute left-0 -translate-x-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-foreground" />
        )}
        <div className="bg-foreground text-background px-3 py-2 rounded-lg shadow-xl">
          <p className="font-semibold text-sm">{hotspot.label}</p>
          <p className="text-xs text-background/70">{hotspot.description}</p>
        </div>
        {/* Tooltip arrow - bottom (for tooltip above) */}
        {!tooltipBelow && !tooltipLeft && !tooltipRight && (
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-foreground" />
        )}
      </div>
    </div>
  );
};

export const HeroSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { hotspots, addHotspot, updateHotspot, deleteHotspot } = useHotspots();
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // Listen for Ctrl+Shift+H (Windows/Linux) or Cmd+Shift+H (Mac) keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'H' || e.key === 'h')) {
        e.preventDefault();
        // Check if already authenticated
        const token = sessionStorage.getItem('hotspot_admin_token');
        if (token) {
          setIsEditMode(true);
        } else {
          setShowAdminDialog(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/auth?mode=signup");
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-background via-background/95 to-background/90">
        {/* Floating animated purple gradient orbs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-primary/30 via-purple-400/20 to-transparent rounded-full blur-3xl opacity-70 transform -translate-x-1/3 -translate-y-1/3 animate-float-slow"></div>
          <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-purple-400/25 via-primary/15 to-transparent rounded-full blur-3xl opacity-60 transform translate-x-1/4 animate-float-medium animation-delay-2s"></div>
          <div className="absolute bottom-0 left-1/3 w-[700px] h-[700px] bg-gradient-to-tr from-primary/20 via-purple-400/15 to-transparent rounded-full blur-3xl opacity-50 transform translate-y-1/3 animate-float-fast animation-delay-4s"></div>
          <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-gradient-to-tl from-purple-400/30 via-primary/20 to-transparent rounded-full blur-3xl opacity-40 animate-float-slow animation-delay-6s"></div>
          <div className="absolute top-3/4 left-1/4 w-[350px] h-[350px] bg-gradient-to-br from-primary/25 via-purple-400/20 to-transparent rounded-full blur-3xl opacity-45 animate-float-medium animation-delay-1s"></div>
          <div className="absolute top-1/6 left-2/3 w-[450px] h-[450px] bg-gradient-to-tl from-purple-400/20 via-primary/25 to-transparent rounded-full blur-3xl opacity-35 animate-float-fast animation-delay-3s"></div>
        </div>
      </div>

      <div className="container mx-auto px-6 relative pt-20 md:pt-56 pb-32">
        <div className="flex justify-center items-center md:items-start min-h-[calc(100vh-10rem)] md:min-h-[50vh]">
          {/* Centered Content */}
          <div className="space-y-8 text-center max-w-4xl">
            {/* Social Proof Badge */}
            <div className="flex justify-center">
              <a href="/student" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 hover:scale-105 transition-all duration-300 cursor-pointer group">
                <span className="bg-primary text-primary-foreground px-2 py-0.5 rounded-full text-xs font-bold">NEW!</span>
                Welcome Students!
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>

            {/* Main Heading */}
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-[1.1]">
                Time is money.
                <br />
                <span className="bg-gradient-to-r from-primary via-[hsl(280,60%,60%)] to-[hsl(290,70%,70%)] bg-clip-text text-transparent">
                  Save both.
                </span>
              </h1>

              {/* Description */}
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Easy-to-use task management, group collaboration, and project tracking. All in one beautiful place.
              </p>
            </div>

            {/* Email CTA */}
            <div className="space-y-4 flex flex-col items-center">
              <div className="flex items-center gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-primary via-[hsl(280,60%,60%)] to-[hsl(290,70%,70%)] hover:from-primary/90 hover:via-[hsl(280,60%,55%)] hover:to-[hsl(290,70%,65%)] text-white font-semibold px-6"
                  onClick={handleGetStarted}
                >
                  {user ? "Go to Dashboard" : "Get started for free"}
                </Button>
                {!user && (
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    <div>Free forever.</div>
                    <div>No credit card.</div>
                  </div>
                )}
              </div>

              <button
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => navigate("/whyqueueos")}
              >
                <span>Why queueOS</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Preview with Hotspots - Hidden on mobile */}
        <div className="hidden md:block relative max-w-5xl mx-auto mt-12 px-6">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/50 bg-card">
            <img
              ref={imageRef}
              src={resolvedTheme === 'dark' ? heroDashboardDark : heroDashboardLight}
              alt="queueOS Dashboard"
              className="w-full h-auto"
              loading="eager"
              fetchPriority="high"
            />
            
            {/* Hotspot Points - only show when not in edit mode */}
            {!isEditMode && hotspots.map((hotspot) => (
              <HotspotPoint key={hotspot.id} hotspot={hotspot} />
            ))}

            {/* Editor Overlay */}
            {isEditMode && (
              <HotspotEditor
                hotspots={hotspots}
                onAdd={addHotspot}
                onUpdate={updateHotspot}
                onDelete={deleteHotspot}
                onClose={() => setIsEditMode(false)}
                imageRef={imageRef}
              />
            )}
          </div>
          
          {/* Edit mode indicator */}
          {isEditMode && (
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium shadow-lg">
              Edit Mode
            </div>
          )}
        </div>
      </div>

      {/* Admin Dialog */}
      <HotspotAdminDialog
        open={showAdminDialog}
        onOpenChange={setShowAdminDialog}
        onSuccess={() => setIsEditMode(true)}
      />
    </section>
  );
};
